// Inventory > Movements: filter model + request builder. Pure (no React) so it stays testable.
// The server owns every number (exact text); nothing here adds or rounds values.

export const PERIODS = ['30D', '3M', '6M', '12M', '24M', 'All', 'Custom'];   // same keys as Inventory > Price Analysis
const PERIOD_MONTHS = { '3M': 3, '6M': 6, '12M': 12, '24M': 24 };
export const DEFAULT_PERIOD = '30D';
export const PAGE_SIZE = 25;
export const MOVEMENT_TYPES = ['purchase', 'production', 'consumption', 'sale', 'mortality', 'transfer', 'adjustment', 'opening'];
export const NODE_TYPES = ['storage', 'band'];

// [key, colour]. Predicates live on the server (lib/inventoryMovements.js QUICK_TESTS) so counts and paging stay right.
export const QUICK = [
  ['recv', '#2d6a4f'], ['disp', '#b14a30'], ['farm', '#c47d10'], ['xfer', '#33598c'], ['adj', '#7a6a4f'], ['notes', '#6a786e'],
];

const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayIso = () => iso(new Date());

// 30D = today and the 29 days before it (30 calendar days, both ends inclusive; the server filters date >= from and <= to).
export function periodRange(per, now = new Date()) {
  if (per === 'All') return { from: '', to: '' };
  if (per === '30D') { const d = new Date(now); d.setDate(d.getDate() - 29); return { from: iso(d), to: iso(now) }; }
  const months = PERIOD_MONTHS[per];
  if (!months) return null;                 // Custom: the user's own dates
  const d = new Date(now);
  d.setMonth(d.getMonth() - months);
  return { from: iso(d), to: iso(now) };
}

export function initialFilters() {
  return { q: '', per: DEFAULT_PERIOD, ...periodRange(DEFAULT_PERIOD), item: [], cat: [], storage: [], band: [], type: [], partner: [], node: [], quick: [] };
}

export const setPeriod = (f, per) => ({ ...f, per, ...(periodRange(per) || {}) });

const FACETS = ['item', 'cat', 'storage', 'band', 'type', 'partner', 'node'];

// The Roll-forward honours only the scope dimensions (+ period); the Ledger honours everything. Each tab filters on its own state.
export const ROLL_FACETS = ['item', 'cat', 'storage', 'band', 'node'];
export const facetsFor = (roll) => (roll ? ROLL_FACETS : FACETS);

export function activeCount(f, roll = false) {
  return facetsFor(roll).filter((k) => f[k].length).length + (roll ? 0 : (f.q ? 1 : 0) + (f.quick.length ? 1 : 0)) + (f.per !== DEFAULT_PERIOD ? 1 : 0);
}

// Filters -> query string for GET /api/inventory/movements.
const PARAM_OF = { item: 'item_id', cat: 'category', storage: 'storage_id', band: 'band_id', type: 'movement_type', partner: 'partner_id', node: 'node_type', quick: 'quick' };

export function toQuery(f, extra = {}) {
  const p = new URLSearchParams();
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  const roll = String(extra.include || '').includes('rollforward');   // the Roll-forward never sends ledger-only params (q, quick, type, partner)
  if (f.q && !roll) p.set('q', f.q);
  facetsFor(roll).concat(roll ? [] : 'quick').forEach((k) => { if (f[k].length) p.set(PARAM_OF[k], f[k].join(',')); });
  Object.entries(extra).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.set(k, v); });
  return p.toString();
}

// A filter patch that toggles one value inside a facet array (used by the by-type rows and the roll-forward jump).
export const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

// ---- sorting (server-side across the whole filtered set; null = natural order, newest first) ----
// Date is the natural order itself (asc = oldest first); the running Balance columns are not sortable (they only read in natural order).
export const SORTABLE = ['date', 'type', 'item', 'storage', 'unit', 'in', 'out', 'cp', 'ref', 'reference', 'notes', 'band', 'fromto', 'reason', 'uc', 'val', 'line'];
export const inNaturalOrder = (sort) => !sort || sort.key === 'date';

// Header click cycle: asc -> desc -> natural. Date has no "desc" step (desc IS natural): asc -> natural.
export function nextSort(cur, key) {
  const same = cur && cur.key === key;
  if (key === 'date') return same ? null : { key, dir: 'asc' };
  if (!same) return { key, dir: 'asc' };
  return cur.dir === 'asc' ? { key, dir: 'desc' } : null;
}

// ---- faceted options (cross-filtering) ----
// opt = [{value,label,...}]. counts = {value: n} from the server's `facets` block, or null before the first load.
// Selected values always stay listed (count 0 when the other filters leave them no rows, with a hint); with counts every other
// option must have rows under the other filters. Counts show as a hint, e.g. "Table Eggs (214)".
export function narrow(opts, counts, selected, zeroTitle) {
  if (!counts) return opts;
  return opts
    .filter((o) => counts[o.value] || selected.includes(o.value))
    .map((o) => ({ ...o, count: counts[o.value] || 0, hint: `(${counts[o.value] || 0})`, title: counts[o.value] ? undefined : zeroTitle }));
}

// ---- grouped options (same grouping as ItemOptgroups / StorageOptgroups) ----
// Level 1 = category-constant display_label in constant sort order (unknown sink last, then alpha); level 2 (items only) =
// item-category name alpha; options alpha within. `order` = constant keys in sort_order, `labelOf(constant)` = live display_label.
// Returns flat options carrying `group` / `subgroup`, sorted so equal groups are consecutive (what FacetMenu renders).
export function groupOptions(rows, order, labelOf) {
  const rank = (c) => { const i = order.indexOf(c); return i < 0 ? 999 : i; };
  const cmp = (a, b) => String(a || '').localeCompare(String(b || ''));
  return rows
    .map((r) => ({ ...r, group: labelOf(r.cat), subgroup: r.sub || undefined, _r: rank(r.cat) }))
    .sort((a, b) => a._r - b._r || cmp(a.cat, b.cat) || cmp(a.subgroup, b.subgroup) || cmp(a.label, b.label))
    .map(({ cat, sub, _r, ...o }) => o);
}

// ---- names: server-provided first, lookup maps second, a short id last (never a bare dash for an id that exists) ----
export const shortId = (id) => (id ? `#${String(id).slice(0, 8)}` : '—');
export const pickName = (serverName, map, id) => serverName || map?.[id]?.name || shortId(id);

// Which lookup lists (items / storages / bands / partners) lack an id the loaded response mentions.
export function missingKinds(data, look) {
  const miss = new Set();
  const chk = (kind, by, id) => { if (id && !by[id]) miss.add(kind); };
  const rows = [...(data.rows || [])];
  rows.forEach((r) => {
    chk('items', look.itemBy, r.item_id); chk('partners', look.partnerBy, r.partner_id); chk('bands', look.bandBy, r.band_id);
    chk('storages', look.storageBy, r.storage_id);
  });
  (data.by_item || []).forEach((a) => chk('items', look.itemBy, a.item_id));
  (data.rollforward || []).forEach((r) => { chk('items', look.itemBy, r.item_id); chk(r.node_type === 'band' ? 'bands' : 'storages', r.node_type === 'band' ? look.bandBy : look.storageBy, r.node_id); });
  return [...miss].sort();
}
